<template>
  <div
    v-if="job"
    :class="[
      'job-details-panel',
      { 'job-details-panel--plain': context === 'company' }
    ]"
  >
    <div class="job-detail-header">
      <div class="job-header-section">
        <div class="job-title-section">
          <div class="title-with-tags">
            <h2 class="detail-job-title">{{ job.title || job.company_name }}</h2>
            <div class="job-tags" v-if="job.recruitment_type">
              <span class="tag" :class="getRecruitmentTypeClass(job.recruitment_type)">
                {{ getRecuitmenntLabel(job.recruitment_type) }}
              </span>
            </div>
          </div>
        </div>
        <div class="salary-info">
          <div class="action-icons">
            <span class="icon">
              <img :src="shareIcon" alt="分享" />
            </span>
            <span
              class="icon favorite-icon"
              :class="{ active: job.is_favorite }"
              @click.stop="$emit('favorite', job)"
              :title="job.is_favorite ? '取消收藏' : '收藏职位'"
            >
              <img
                :src="job.is_favorite ? favoriteActiveIcon : favoriteDefaultIcon"
                :alt="job.is_favorite ? '已收藏' : '收藏'"
              />
            </span>
            <span class="icon">
              <img :src="reportIcon" alt="举报" />
            </span>
          </div>
          <span class="salary">{{ formatSalary(job.salary_start, job.salary_end, job.salary_month) || job.salary }}</span>
        </div>
      </div>
      <div class="job-meta">
        <div class="meta-content">
          <div class="job-basic-info">
            <span class="info-item" v-if="job.job_address">
              <img :src="addressIcon" alt="地址" class="info-icon" />
              <span class="info-text">{{ job.job_address }}</span>
            </span>
            <span v-if="job.work_nature">
              <img :src="attributeIcon" alt="属性" class="info-icon">
              {{ getWorkNatureLabel(job.work_nature) }}
            </span>
            <span>{{ getEducationLabel(job.education) }}</span>
            <span>{{ getExperienceText(job.experience) }}</span>
          </div>
          <div class="update-info">
            <span v-if="job.publish_time">更新于: {{ formatDate(job.publish_time) }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="job-description-section" v-if="job.description || job.requirement || job.job_keywords">
      <h3 class="section-title">职位描述</h3>
      <div class="job-tags" v-if="job.job_keywords">
        <span class="tag" v-for="keyword in job.job_keywords.split(',')" :key="keyword">{{ keyword.trim() }}</span>
      </div>
      <div class="responsibilities" v-if="job.description">
        <h4>岗位职责:</h4>
        <ul v-html="formatDescription(job.description)"></ul>
      </div>
      <div class="qualifications" v-if="job.requirement">
        <h4>任职资格</h4>
        <ul v-html="formatDescription(job.requirement)"></ul>
      </div>
    </div>

    <div class="work-location-section" v-if="job.job_address && !hideMap">
      <h3 class="section-title">工作地点</h3>
      <div class="location-map" style="position: relative;">
        <div :ref="mapContainerRef" class="amap-container"></div>
        <div class="map-placeholder" v-if="mapLoading">
          <div class="map-content">
            <div class="location-pin">📍</div>
            <div class="location-text">{{ job.job_address }}</div>
            <div class="location-text">正在加载地图...</div>
          </div>
        </div>
      </div>
    </div>

    <div class="company-info-section" v-if="showCompanyInfo && companyData">
      <h3 class="section-title">公司信息</h3>
      <div class="company-card">
        <div class="company-logo">
          <img :src="companyData.logo" :alt="companyData.name">
        </div>
        <div class="company-details">
          <div class="company-name">{{ companyData.name }}</div>
          <div class="company-info-text">
            {{ companyData.industry }}<span v-if="companyData.city"> | {{ companyData.city }}</span>
          </div>
          <div class="company-info-text" v-if="companyData.description">
            {{ companyData.description }}
          </div>
        </div>
        <div class="company-arrow">→</div>
      </div>
    </div>

    <div class="action-buttons" v-if="showActions">
      <button class="btn-interview" @click="$emit('interview', job)">面试此岗</button>
      <button class="btn-apply" @click="$emit('apply', job)">开始投递</button>
    </div>

    <slot></slot>
  </div>
</template>

<script>
import shareIcon from '@/views/index/assets/img/share.png'
import favoriteDefaultIcon from '@/views/index/assets/img/star.png'
import favoriteActiveIcon from '@/views/hot-jobs/assets/img/favorite.png'
import reportIcon from '@/views/index/assets/img/report.png'
import addressIcon from '@/views/index/assets/img/address.png'
import attributeIcon from '@/views/index/assets/img/attribute.png'
import {
  getRecuitmenntLabel,
  getRecruitmentTypeClass,
  getEducationLabel,
  getWorkNatureLabel,
  formatSalary
} from '@/constants/jobConstants'

export default {
  name: 'JobDetailSection',
  props: {
    job: { type: Object, default: null },
    context: { type: String, default: 'global' },
    mapLoading: { type: Boolean, default: false },
    mapRef: { type: String, default: '' },
    showCompanyInfo: { type: Boolean, default: false },
    company: {
      type: Object,
      default: null
    },
    showActions: { type: Boolean, default: false },
    hideMap: { type: Boolean, default: false }
  },
  data() {
    return {
      shareIcon,
      favoriteDefaultIcon,
      favoriteActiveIcon,
      reportIcon,
      addressIcon,
      attributeIcon
    }
  },
  computed: {
    mapContainerRef() {
      if (this.hideMap) return ''
      if (this.mapRef) return this.mapRef
      return this.context === 'company' ? 'companyJobAmapContainer' : 'amapContainer'
    },
    companyData() {
      if (!this.showCompanyInfo) return null
      const fallbackName = (this.job && this.job.company_name) || ''
      const fallbackLogo = (this.job && this.job.company_logo) || ''
      const fallbackIndustry = (this.job && this.job.category) || ''
      const fallbackCity = (this.job && this.job.city) || ''
      const fallbackDesc = (this.job && this.job.company_profile) || ''
      return {
        name: this.company?.name || fallbackName,
        logo: this.company?.logo || fallbackLogo,
        industry: this.company?.industry || fallbackIndustry,
        city: this.company?.city || fallbackCity,
        description: this.company?.description || fallbackDesc
      }
    }
  },
  methods: {
    formatSalary,
    getRecuitmenntLabel,
    getRecruitmentTypeClass,
    getEducationLabel,
    getWorkNatureLabel,
    getExperienceText(experience) {
      const experienceMap = {
        0: '不限',
        1: '1年以下',
        2: '1-3年',
        3: '3-5年',
        4: '5-10年',
        5: '10年以上'
      }
      return experienceMap[experience] || '不限'
    },
    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('zh-CN')
    },
    formatDescription(text) {
      if (!text) return ''
      const lines = text.split('\n').filter(line => line.trim() !== '')
      return lines.map(line => `<li>${line.trim()}</li>`).join('')
    }
  }
}
</script>

<style scoped lang="scss">
$text-color: #656587;
$gradient-bg: linear-gradient(90deg, #62AFFF 0%, #9C41FF 100%);

.job-details-panel {
  width: 60%;
  background: var(--Linear, linear-gradient(321deg, rgba(255, 255, 255, 0.40) 2.06%, rgba(255, 255, 255, 0.72) 42.17%, rgba(255, 255, 255, 0.80) 99.08%));
  box-shadow: 1px 1px 10px 0 rgba(0, 0, 0, 0.05), 4px 4px 20px 0 rgba(0, 0, 0, 0.05);
  backdrop-filter: blur(15px);
  border-radius: 8px;
  padding: 18px;
  padding-bottom: 0;
  overflow-y: auto;
  border: 1px solid transparent;
  display: flex;
  flex-direction: column;
}

.job-details-panel--plain {
  background: transparent;
  box-shadow: none;
  border: none;
  position: static;
  width: 100%;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.job-detail-header {
  border-bottom: 1px solid #e5e5e5;
  padding-bottom: 20px;
  margin-bottom: 24px;

  .job-header-section {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 16px;
  }

  .job-title-section {
    flex: 1;

    .title-with-tags {
      display: flex;
      align-items: center;
      gap: 12px;

      .detail-job-title {
        font-size: 22px;
        font-weight: 600;
        color: #2f2f2f;
        margin: 0;
        max-width: 420px;
      }

      .job-tags {
        display: flex;
        gap: 8px;

        .tag {
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 12px;
          background: rgba(98, 175, 255, 0.12);
          color: #62AFFF;
        }
      }
    }
  }

  .salary-info {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-shrink: 0;

    .salary {
      font-size: 20px;
      font-weight: 600;
      background: $gradient-bg;
      background-clip: text;
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }

    .action-icons {
      display: flex;
      gap: 8px;

      .icon {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 24px;
        height: 24px;
        cursor: pointer;
        padding: 4px;
        border-radius: 4px;
        transition: all 0.2s ease;

        img {
          width: 16px;
          height: 16px;
          object-fit: contain;
        }

        &:hover {
          background: rgba(98, 175, 255, 0.12);
        }
      }

      .favorite-icon.active {
        background: rgba(98, 175, 255, 0.12);
      }
    }
  }
}

.job-meta {
  .meta-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
  }

  .job-basic-info {
    display: flex;
    gap: 16px;
    flex-wrap: wrap;

    span {
      font-size: 14px;
      color: $text-color;
      display: flex;
      gap: 2px;
      align-items: center;
    }

    .info-item {
      display: flex;
      align-items: center;
      gap: 4px;
      max-width: 90%;
      min-width: 0;

      .info-text {
        flex: 1;
        min-width: 0;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .info-icon {
        width: 14px;
        height: 14px;
        object-fit: contain;
      }
    }
  }

  .update-info {
    font-size: 12px;
    color: #999;
    flex-shrink: 0;
  }
}

.job-description-section {
  padding: 20px;

  .section-title {
    font-size: 18px;
    font-weight: 600;
    color: #333;
    margin-bottom: 16px;
  }

  .job-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 20px;

    .tag {
      padding: 4px 8px;
      background: #f0f0f0;
      border-radius: 4px;
      font-size: 12px;
      color: #666;
    }
  }

  .responsibilities,
  .qualifications {
    margin-bottom: 20px;
    color: $text-color;

    h4 {
      font-size: 16px;
      font-weight: 600;
      color: $text-color;
      margin-bottom: 12px;
    }

    ul {
      margin: 0;
      padding-left: 20px;
      list-style-type: decimal;
      font-size: 14px;

      li {
        font-size: 14px;
        color: $text-color;
        line-height: 1.6;
        margin-bottom: 8px;
        list-style-position: outside;
      }
    }
  }
}

.work-location-section {
  margin-bottom: 24px;

  .section-title {
    font-size: 18px;
    font-weight: 600;
    color: #333;
    margin-bottom: 16px;
  }

  .location-map {
    position: relative;

    .amap-container {
      height: 300px;
      width: 100%;
      border-radius: 8px;
      overflow: hidden;
      border: 1px solid #e5e5e5;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    }

    .map-placeholder {
      height: 200px;
      background: #f5f5f5;
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 1px solid #e5e5e5;

      .map-content {
        text-align: center;

        .location-pin {
          font-size: 24px;
          margin-bottom: 8px;
        }

        .location-text {
          font-size: 14px;
          color: #666;
        }
      }
    }
  }
}

.company-info-section {
  margin-bottom: 24px;

  .section-title {
    font-size: 18px;
    font-weight: 600;
    color: #333;
    margin-bottom: 16px;
  }

  .company-card {
    display: flex;
    align-items: center;
    padding: 16px;
    background: #f8f9fa;
    border-radius: 8px;
    border: 1px solid #e5e5e5;
    cursor: pointer;

    &:hover {
      background: #f0f0f0;
    }

    .company-logo {
      width: 40px;
      height: 40px;
      margin-right: 16px;
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      background: #f5f5f5;
      border-radius: 4px;
      overflow: hidden;

      img {
        width: 100%;
        height: 100%;
        border-radius: 4px;
        object-fit: cover;
        display: block;
      }
    }

    .company-details {
      flex: 1;

      .company-name {
        font-size: 16px;
        font-weight: 600;
        color: #333;
        margin-bottom: 4px;
      }

      .company-info-text {
        font-size: 14px;
        color: #666;
        line-height: 1.5;
      }
    }

    .company-arrow {
      font-size: 20px;
      color: #ccc;
    }
  }
}

.action-buttons {
  display: flex;
  gap: 12px;
  padding: 20px;
  padding-top: 0;
  margin-top: auto;

  button {
    flex: 1;
    padding: 12px 24px;
    border-radius: 24px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: transform 0.2s ease;

    &:active {
      transform: scale(0.98);
    }
  }

  .btn-interview {
    background: white;
    border: 2px solid;
    border-image: $gradient-bg 1;
    background: $gradient-bg;
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  .btn-apply {
    flex: 1;
    padding: 12px 24px;
    background: $gradient-bg;
    border: none;
    color: white;
    border-radius: 24px;
    box-shadow: 0 8px 16px rgba(103, 160, 255, 0.24);
  }
}

.company-job-detail {
  width: 100%;
  position: static;
  max-height: calc(100vh - 220px);
  overflow-y: auto;
}
</style>

